import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useOpenTimeEntry } from "@/hooks/use-open-time-entry";
import { Clock, Pin, PinOff, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

const PIN_KEY = "quanta.hud.pinned";

function elapsedSince(iso) {
  if (!iso) return 0;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return 0;
  return Math.floor((Date.now() - t) / 1000);
}

function formatHms(totalSecs) {
  const sign = totalSecs < 0 ? "-" : "";
  const s = Math.abs(totalSecs);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return `${sign}${h}:${pad(m)}:${pad(sec)}`;
}

/**
 * Phase 21 — Running-time HUD.
 *
 *  * Rendered app-wide (mounted at App root).
 *  * Only visible when we detect an open time_entry.
 *  * Ticks the display every second in a memoized way that never touches
 *    the network — the entry itself is polled by useOpenTimeEntry.
 *  * Persists a pin/unpin (hidden) preference in localStorage so
 *    workers who don't want the HUD can hide it.
 *  * Flips `document.title` while running so cross-tab visibility is
 *    obvious even when the app is in the background.
 */
export default function RunningTimeHUD({ enabled = true }) {
  const { entry, task } = useOpenTimeEntry({ enabled });
  const [pinned, setPinned] = useState(() =>
    localStorage.getItem(PIN_KEY) !== "false",
  );
  const [seconds, setSeconds] = useState(0);
  const originalTitleRef = useRef(document.title);

  // 1-second tick — pure UI, no fetches.
  useEffect(() => {
    if (!entry) return undefined;
    setSeconds(elapsedSince(entry.clock_in_at));
    const t = setInterval(() => {
      setSeconds(elapsedSince(entry.clock_in_at));
    }, 1000);
    return () => clearInterval(t);
  }, [entry?.id, entry?.clock_in_at]);

  const label = useMemo(() => {
    if (task?.title) return task.title;
    if (entry?.worksite_name) return entry.worksite_name;
    return "Clocked in";
  }, [task, entry]);

  // Tab-title flip.
  useEffect(() => {
    if (!entry) {
      document.title = originalTitleRef.current;
      return undefined;
    }
    document.title = `⏱ ${formatHms(seconds)} — ${label}`;
    return () => {
      document.title = originalTitleRef.current;
    };
  }, [entry?.id, seconds, label]);

  if (!entry) return null;
  if (!pinned) {
    return (
      <button
        type="button"
        onClick={() => {
          setPinned(true);
          localStorage.setItem(PIN_KEY, "true");
        }}
        data-testid="running-hud-restore"
        className="fixed bottom-4 right-4 z-[9999] flex h-10 w-10 items-center justify-center rounded-full border border-border bg-background/95 shadow-lg backdrop-blur hover:bg-accent"
        title="Show running-time HUD"
      >
        <Clock className="h-4 w-4 text-secondary" />
      </button>
    );
  }

  const budget = task?.budget_minutes ? Number(task.budget_minutes) : null;
  const budgetSecs = budget ? budget * 60 : null;
  const remaining = budgetSecs ? budgetSecs - seconds : null;
  let progressPct = 0;
  let progressColor = "bg-secondary";
  if (budgetSecs) {
    progressPct = Math.min((seconds / budgetSecs) * 100, 999);
    if (progressPct >= 100) progressColor = "bg-danger";
    else if (progressPct >= 80) progressColor = "bg-warning";
    else progressColor = "bg-secondary";
  }

  return (
    <div
      className={cn(
        "fixed bottom-4 right-4 z-[9999] min-w-[220px] max-w-[280px]",
        "rounded-2xl border border-border bg-background/95 shadow-2xl",
        "backdrop-blur ring-1 ring-primary/10",
      )}
      data-testid="running-hud"
    >
      <div className="flex items-start gap-3 p-3">
        <Link
          to={entry.task_id ? "/app/tasks" : "/app/time"}
          className="flex flex-1 gap-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary/40"
          data-testid="running-hud-open"
        >
          <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-secondary/10 text-secondary ring-1 ring-secondary/30">
            <Clock className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div
              className="font-mono text-lg font-semibold leading-none text-primary"
              data-testid="running-hud-timer"
            >
              {formatHms(seconds)}
            </div>
            <div className="mt-1 truncate text-xs text-muted-foreground" data-testid="running-hud-label">
              {label}
            </div>
          </div>
          <ChevronRight className="mt-1 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        </Link>
        <button
          type="button"
          onClick={() => {
            setPinned(false);
            localStorage.setItem(PIN_KEY, "false");
          }}
          className="grid h-6 w-6 shrink-0 place-items-center rounded-full text-muted-foreground hover:bg-accent"
          data-testid="running-hud-unpin"
          title="Hide HUD"
        >
          <PinOff className="h-3.5 w-3.5" />
        </button>
      </div>
      {budgetSecs && (
        <div className="px-3 pb-3">
          <div className="mb-1 flex items-center justify-between text-[10px] uppercase tracking-widest text-muted-foreground">
            <span>Budget · {budget}m</span>
            <span
              className={cn(
                "font-mono",
                progressPct >= 100 && "text-danger font-semibold",
                progressPct >= 80 && progressPct < 100 && "text-warning",
              )}
              data-testid="running-hud-remaining"
            >
              {remaining >= 0 ? formatHms(remaining) : formatHms(remaining)}
            </span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-accent">
            <div
              className={cn("h-full transition-[width]", progressColor)}
              style={{ width: `${Math.min(progressPct, 100)}%` }}
              data-testid="running-hud-progress"
            />
          </div>
        </div>
      )}
    </div>
  );
}
